import React, { useState, useRef, useEffect } from 'react';
import { Box, IconButton, TextField, Typography } from '@mui/material';
import {
  Add,
  Remove,
  Check,
  Close,
  Architecture,
} from '@mui/icons-material';

interface SurfaceJointSpinnerProps {
  value: number;
  onChange: (newValue: number) => void;
  jointName?: string;
  min?: number;
  max?: number;
  step?: number;
  showNumber?: boolean;
  icon?: React.ReactNode;
  color?: string;
  onOpen?: () => void;
  onClose?: () => void;
}

const SurfaceJointSpinner: React.FC<SurfaceJointSpinnerProps> = ({
  value,
  onChange,
  jointName,
  min = 0,
  max = 100,
  step = 1,
  showNumber = true,
  color = 'var(--toolbar-button-border)',
  icon = (
    <Architecture
      sx={{
        fontSize: 24,
        transform: 'rotate(-90deg)',
      }}
    />
  ),
  onOpen,
  onClose,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value);
  const holdTimeout = useRef<NodeJS.Timeout | null>(null);
  const holdInterval = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const themeColor = color;

  const getStepDecimals = (s: number): number => {
    if (s % 1 === 0) return 0;
    const str = s.toString();
    const parts = str.split('.');
    return parts[1]?.length ?? 0;
  };

  const stepDecimals = getStepDecimals(step);

  const clampWithStep = (v: number): number => {
    const factor = Math.pow(10, stepDecimals);
    const stepsFromMin = Math.round((v - min) * factor);
    const stepInt = Math.round(step * factor);
    const snappedSteps = Math.round(stepsFromMin / stepInt) * stepInt;
    let result = min + snappedSteps / factor;
    result = Math.max(min, Math.min(max, result));
    return parseFloat(result.toFixed(stepDecimals));
  };

  const formatNumber = (v: number): string => {
    if (step % 1 === 0) {
      return Math.round(v).toFixed(1);
    }
    const decimalPlaces = step.toString().split('.')[1]?.length || 0;
    return v.toFixed(decimalPlaces);
  };
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;

    if (!/^-?\d*\.?\d*$/.test(raw)) return;

    const parsed = parseFloat(raw);
    if (isNaN(parsed)) {
      setTempValue(min);
      return;
    }

    setTempValue(clampWithStep(parsed));
  };

  const handleApply = () => {
    onChange(tempValue);
    setIsEditing(false);
    onClose?.();
  };

  const handleCancel = () => {
    setTempValue(value);
    setIsEditing(false);
    onClose?.();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isEditing &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setTempValue(value);
        setIsEditing(false);
        onClose?.();
      }
    };

    document.addEventListener('pointerdown', handleClickOutside);

    return () => {
      document.removeEventListener('pointerdown', handleClickOutside);
    };
  }, [isEditing, value, onClose]);

  /*   const startHold = (direction: 'inc' | 'dec') => {
      stopHold();
  
      const action = () => {
        setTempValue((prev) =>
          clampWithStep(prev + (direction === 'inc' ? step : -step))
        );
      };
  
      action();
  
      holdTimeout.current = setTimeout(() => {
        holdInterval.current = setInterval(action, 60);
      }, 300);
    }; */

  const startHold = (direction: 'inc' | 'dec') => {
    stopHold();

    const action = () => {
      setTempValue((prev) => {
        let nextValue = clampWithStep(prev + (direction === 'inc' ? step : -step));

        if (jointName?.toLowerCase() === 'thickness' && nextValue === 3) {
          nextValue = clampWithStep(nextValue + (direction === 'inc' ? step : -step));
        }

        return nextValue;
      });
    };

    action();

    holdTimeout.current = setTimeout(() => {
      holdInterval.current = setInterval(action, 60);
    }, 300);
  };


  const stopHold = () => {
    if (holdTimeout.current) {
      clearTimeout(holdTimeout.current);
      holdTimeout.current = null;
    }
    if (holdInterval.current) {
      clearInterval(holdInterval.current);
      holdInterval.current = null;
    }
  };

  useEffect(() => {
    return () => {
      stopHold();
    };
  }, []);

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'inline-block',
        transition: 'all 0.15s ease',
      }}
    >
      {!isEditing ? (
        <Typography
          component="div"
          data-voice-open="1"
          onClick={() => {
            setTempValue(value);
            setIsEditing(true);
            onOpen?.();
          }}
        >
          {showNumber ? (
            <Typography
              sx={{
                fontSize: '10px',
                fontWeight: 'bold',
                color: themeColor,
                fontFamily: 'monospace',
                border: `1px solid ${themeColor}`,
                cursor: 'pointer',
                textAlign: 'center',
                padding: '1px 4px',
                borderRadius: '20px',
                bgcolor: 'var(--spinner-bg)',
                backdropFilter: 'blur(8px)',
                whiteSpace: 'nowrap',
                alignItems: 'center',
                '&:hover': {
                  bgcolor: 'var(--spinner-hover-bg)',
                },
              }}
            >
              {formatNumber(value)}
            </Typography>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: `1px solid ${themeColor}`,
                borderRadius: '50%',
                bgcolor: 'var(--spinner-bg)',
                width: 26,
                height: 26,
                cursor: 'pointer',
                transition: '0.15s ease',
                color: themeColor,
                '&:hover': {
                  bgcolor: 'var(--spinner-hover-bg)',
                },
              }}
            >
              {icon}
            </Box>
          )}
        </Typography>
      ) : (
        <Box
          sx={{
            bgcolor: 'var(--spinner-bg)',
            backdropFilter: 'blur(8px)',
            borderRadius: '12px',
            border: `1px solid ${themeColor}`,
            padding: '6px 10px',
            minWidth: '130px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            textAlign: 'center',
            whiteSpace: 'nowrap',
            transition: '0.15s ease',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 0.5,
            }}
          >
            <Typography
              sx={{
                fontSize: '10px',
                fontWeight: 500,
                color: themeColor,
                letterSpacing: '0.3px',
                fontFamily: 'monospace',
                textAlign: 'center',
                width: '100%',
              }}
            >
              {jointName}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.8,
            }}
          >
            <IconButton
              size="medium"
              data-voice-close="1"
              onClick={handleCancel}
              sx={{
                color: '#d32f2f',
                width: 18,
                height: 18,
              }}
            >
              <Close sx={{ fontSize: 18 }} />
            </IconButton>

            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                bgcolor: 'var(--spinner-input-bg)',
                borderRadius: '20px',
                padding: '1px',
              }}
            >
              <IconButton
                size="small"
                onPointerDown={(e) => {
                  e.preventDefault();
                  startHold('dec');
                }}
                onPointerUp={stopHold}
                onPointerLeave={stopHold}
                onPointerCancel={stopHold}
                disabled={tempValue <= min}
                sx={{
                  bgcolor: 'transparent',
                  color: themeColor,
                  width: 24,
                  height: 24,
                  borderRadius: '12px',
                }}
              >
                <Remove sx={{ fontSize: 14 }} />
              </IconButton>

              <TextField
                type="text"
                value={formatNumber(tempValue)}
                onChange={handleInputChange}
                variant="standard"
                size="small"
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    width: '36px',
                    '& input': {
                      textAlign: 'center',
                      padding: '2px 0',
                      fontSize: '11px',
                      color: themeColor,
                      bgcolor: 'transparent',
                      fontFamily: 'monospace',
                      fontWeight: 500,
                    },
                  },
                }}
              />

              <IconButton
                size="small"
                onPointerDown={(e) => {
                  e.preventDefault();
                  startHold('inc');
                }}
                onPointerUp={stopHold}
                onPointerLeave={stopHold}
                onPointerCancel={stopHold}
                disabled={tempValue >= max}
                sx={{
                  bgcolor: 'transparent',
                  color: themeColor,
                  width: 24,
                  height: 24,
                  borderRadius: '12px',
                }}
              >
                <Add sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>

            <IconButton
              size="small"
              onClick={handleApply}
              sx={{
                color: '#2e7d32',
                width: 18,
                height: 18,
              }}
            >
              <Check sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default SurfaceJointSpinner;
